import * as styleConstants from "@affinity/constants/StyleConstants";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import Breadcrumbs, { BreadcrumbsPresentationProps } from "@affinity/MobiusOverrides/Breadcrumbs";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { LinkProps } from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        links: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        homePageUrl: getHomePageUrl(state),
        isAuthenticated: state.context.session.isAuthenticated,
    };
};

export interface PageBreadcrumbsStyles {
    breadcrumbs?: BreadcrumbsPresentationProps;
}

export const pageBreadcrumbStyles: PageBreadcrumbsStyles = {
    breadcrumbs: {
        css: css`
            color: ${styleConstants.colours.darkBlue};
            padding: 1px 0;
            > span:last-child {
                color: ${styleConstants.colours.darkGrey};
            }
            margin: 0;
            @media (min-width: ${styleHelpers.rem(1024)}) {
                margin: 0 0 0 ${styleHelpers.rem(46)};
            }
            @media (min-width: ${styleHelpers.rem(1280)}) {
                margin: 0 0 0 ${styleHelpers.rem(105)};
            }
        `,
        typographyProps: {
            css: css`
                font-weight: 400;
                font-size: ${styleHelpers.rem(14)};
                line-height: ${styleHelpers.rem(18)};
                padding: 0;
                margin: 0;
            `,
        },
        iconProps: {
            src: "ChevronRight",
            color: styleConstants.colours.darkBlue,
            size: 10,
            css: css`
                padding: 0 ${styleHelpers.rem(16)};
                height: 100%;
                width: auto;
            `,
        },
    },
};

export const pageMobileBreadcrumbStyles: PageBreadcrumbsStyles = {
    breadcrumbs: {
        css: css`
            color: ${styleConstants.colours.darkBlue};
            padding: 0;
            > span:nth-last-of-type(-n + 2) {
                display: none;
            }
            > a span:hover {
                color: ${styleConstants.colours.errorRed}!important;
                text-decoration-line: underline;
            }
        `,
        typographyProps: {
            css: css`
                font-weight: 400;
                font-size: ${styleHelpers.rem(14)};
                line-height: ${styleHelpers.rem(18)};
                padding: 0;
                margin: 0;
            `,
        },
        iconProps: {
            src: "ChevronLeft",
            color: styleConstants.colours.darkBlue,
            size: 10,
            css: css`
                padding: 0 ${styleHelpers.rem(16)};
                height: 100%;
                width: auto;
            `,
        },
    },
};

type Props = ReturnType<typeof mapStateToProps>;

const PageBreadcrumbs = (props: Props) => {
    if (!props.isAuthenticated) {
        return <></>;
    }

    let links = props.links || generateLinksFrom(props.linksState, props.nodeId, props.homePageUrl);
    let linksBasic: LinkProps[] = [];
    if (links.length > 0) {
        // we need to always direct to the language-specific homepage URL
        const homePageLink = { children: translate("Home_Breadcrumb"), href: props.homePageUrl };
        const linksWithoutHomePageLink = [...links].splice(1, links.length);
        links = [homePageLink, ...linksWithoutHomePageLink];
        linksBasic = linksWithoutHomePageLink; // Or should this be last but 1 from links??? IE immediate parent ???
    }

    const mobileFirstIcon: IconProps = {
        src: "ChevronLeft",
        color: styleConstants.colours.darkBlue,
        size: 12,
        css: css`
            padding: 0 ${styleHelpers.rem(16)} 0 0;
            height: 100%;
            width: auto;
        `,
    };

    const mobileWrapper: HiddenProps = {
        css: css`
            display: flex;
            align-items: center;
            padding: 0;
            margin: 0;
        `,
    };

    return (
        <>
            <Hidden below="lg">
                <Page as="div">
                    <Breadcrumbs
                        sepIcon={ChevronRight}
                        links={links}
                        data-test-selector="pageBreadcrumbs"
                        {...pageBreadcrumbStyles.breadcrumbs}
                    />
                </Page>
            </Hidden>
            <Hidden above="md" {...mobileWrapper}>
                {links.length < 2 ? (
                    <Page as="div">
                        <Icon {...mobileFirstIcon} />
                        <Breadcrumbs
                            sepIcon={ChevronLeft}
                            links={linksBasic}
                            data-test-selector="pageBreadcrumbs"
                            {...pageMobileBreadcrumbStyles.breadcrumbs}
                        />
                    </Page>
                ) : null}
            </Hidden>
        </>
    );
};

export function generateLinksFrom(linksState: LinksState, nodeId: string, homePageUrl: string) {
    const links: LinkProps[] = [];
    let currentLink = getPageLinkByNodeId({ links: linksState }, nodeId);

    while (currentLink) {
        links.unshift({ children: currentLink.title, href: currentLink.url });
        currentLink = currentLink.parentId
            ? getPageLinkByNodeId({ links: linksState }, currentLink.parentId)
            : undefined;
    }

    return links;
}

export default connect(mapStateToProps)(PageBreadcrumbs);
